import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { get, put } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";

const PatientTaskCompletion = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completionModal, setCompletionModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [userResponse, setUserResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await get("/api/app/cbt/tasks");
      
      if (response?.success) {
        setTasks(response.data.tasks || []);
      } else {
        toastMessage("Failed to fetch tasks", "error");
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toastMessage("Failed to fetch tasks", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleCompleteTask = (task) => {
    setSelectedTask(task);
    setUserResponse("");
    setCompletionModal(true);
  };

  const handleSubmitCompletion = async () => {
    if (!selectedTask || !userResponse.trim()) {
      toastMessage("Please provide your response", "error");
      return;
    }

    try {
      setSubmitting(true);
      const response = await put(`/api/app/cbt/tasks/${selectedTask._id}/status`, {
        status: "COMPLETED",
        userResponse: userResponse.trim()
      });

      if (response?.success) {
        toastMessage("Task completed successfully!", "success");
        setCompletionModal(false);
        setSelectedTask(null);
        setUserResponse("");
        fetchTasks(); // Refresh the task list
      } else {
        toastMessage(response?.message || "Failed to complete task", "error");
      }
    } catch (error) {
      console.error("Error completing task:", error);
      toastMessage("Failed to complete task", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "IN_PROGRESS":
        return "warning";
      case "OVERDUE":
        return "error";
      case "ASSIGNED":
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircleIcon color="success" />;
      case "IN_PROGRESS":
        return <PlayArrowIcon color="warning" />;
      case "OVERDUE":
        return <ScheduleIcon color="error" />;
      case "ASSIGNED":
      default:
        return <AssignmentIcon color="action" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No due date";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            My CBT Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete your assigned tasks to track your progress
          </Typography>
        </Box>

        {/* Task Statistics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {tasks.filter(t => t.status === "ASSIGNED").length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Assigned Tasks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {tasks.filter(t => t.status === "IN_PROGRESS").length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In Progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {tasks.filter(t => t.status === "COMPLETED").length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="error.main">
                  {tasks.filter(t => t.status === "OVERDUE").length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overdue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tasks Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Task List
            </Typography>
            
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : tasks.length === 0 ? (
              <Alert severity="info">
                No tasks assigned yet. Tasks will appear here once assigned by your therapist.
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Session</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.map((task) => (
                      <TableRow key={task._id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {task.title || "Untitled Task"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {task.description || "No description available"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            Session {task.sessionNo} - {task.sessionType}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(task.status)}
                            label={task.status.replace("_", " ")}
                            color={getStatusColor(task.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(task.dueDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box sx={{ width: "100%", mr: 1 }}>
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 8,
                                  backgroundColor: "grey.200",
                                  borderRadius: 4,
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${task.progress || 0}%`,
                                    height: "100%",
                                    backgroundColor: 
                                      task.progress === 100 ? "success.main" :
                                      task.progress > 50 ? "warning.main" :
                                      "primary.main",
                                    transition: "width 0.3s ease",
                                  }}
                                />
                              </Box>
                            </Box>
                            <Typography variant="caption">
                              {task.progress || 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {task.status === "ASSIGNED" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleCompleteTask(task)}
                            >
                              Complete
                            </Button>
                          )}
                          {task.status === "IN_PROGRESS" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => handleCompleteTask(task)}
                            >
                              Continue
                            </Button>
                          )}
                          {task.status === "COMPLETED" && (
                            <Chip
                              label="Completed"
                              color="success"
                              size="small"
                            />
                          )}
                          {task.status === "OVERDUE" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => handleCompleteTask(task)}
                            >
                              Complete Now
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Task Completion Modal */}
        <Dialog 
          open={completionModal} 
          onClose={() => setCompletionModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Complete Task
            {selectedTask && (
              <Typography variant="body2" color="text.secondary">
                Session {selectedTask.sessionNo} - {selectedTask.sessionType}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {selectedTask && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedTask.title || "Untitled Task"}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {selectedTask.description || "No description available"}
                </Typography>
                
                <Typography variant="subtitle1" gutterBottom>
                  Your Response *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Please describe how you completed this task, what you learned, or any challenges you faced..."
                  value={userResponse}
                  onChange={(e) => setUserResponse(e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Tip:</strong> Be specific about what you did, how it made you feel, and what you learned from this task.
                  </Typography>
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setCompletionModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitCompletion}
              variant="contained"
              color="success"
              disabled={!userResponse.trim() || submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {submitting ? "Completing..." : "Mark as Completed"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default PatientTaskCompletion; 